import { useState } from 'react'
import { Visibility, VisibilityOff } from '@mui/icons-material'
import { Box, Button, IconButton, Typography } from '@mui/material'
import { Formik, FormikHelpers } from 'formik'
import { enqueueSnackbar } from 'notistack'
import * as Yup from 'yup'

import Api from '../../utils/api'
import useIsMobile from '../../utils/hooks/useIsMobile'
import FormikTextField from '../formik-input'
import FullScreenModal from '../full-screen-modal'

import useStyles from './index.styles'

interface IChangePasswordFormValues {
  currentPassword: string
  newPassword: string
}

interface Props {
  open: boolean
  onClose: () => void
}

const currentPasswordValidationSchema = Yup.object().shape({
  currentPassword: Yup.string().required('Current password is required'),
})

const newPasswordValidationSchema = Yup.object().shape({
  newPassword: Yup.string()
    .required('New password is required')
    .min(8, 'New password must be at least 8 characters long')
    .matches(/\d/, 'New password must include at least one number'),
})

const ChangePasswordModal = (props: Props) => {
  const { open, onClose } = props

  const classes = useStyles
  const isMobile = useIsMobile()

  const [currentPasswordValue, setCurrentPasswordValue] = useState<string | null>(null)
  const [showCurrentPassword, setShowCurrentPassword] = useState<boolean>(false)
  const [showNewPassword, setShowNewPassword] = useState<boolean>(false)

  const resetModalState = () => {
    setCurrentPasswordValue(null)
    setShowCurrentPassword(false)
    setShowNewPassword(false)
  }

  const handleSubmit = async (values: IChangePasswordFormValues, helpers: FormikHelpers<IChangePasswordFormValues>) => {
    if (!currentPasswordValue) {
      setCurrentPasswordValue(values.currentPassword)
      helpers.setSubmitting(false)
      return
    }

    const res = await Api.post('/api/authentication/changepassword', {
      newPassword: values.newPassword,
      oldPassword: currentPasswordValue,
    })

    if (!res.ok) {
      enqueueSnackbar('Failed to change password', { variant: 'error' })
    } else {
      enqueueSnackbar('Password has been changed', { variant: 'success' })
      resetModalState()
      helpers.resetForm()
      onClose()
    }

    helpers.setSubmitting(false)
  }

  const endAdornment = (
    <IconButton aria-label="toggle password visibility" onClick={() => setShowCurrentPassword((prev) => !prev)} edge="end">
      {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
    </IconButton>
  )

  const validationSchema = currentPasswordValue ? newPasswordValidationSchema : currentPasswordValidationSchema

  return (
    <Formik
      key={open ? 'change-password-open' : 'change-password-closed'}
      initialValues={{ currentPassword: '', newPassword: '' }}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ values, isSubmitting, resetForm, submitForm }) => (
        <FullScreenModal
          open={open}
          onClose={() => {
            resetModalState()
            resetForm()
            onClose()
          }}
          showCloseIcon={true}
          title="Change Password"
          confirmButton={
            <Button
              type="button"
              onClick={submitForm}
              variant="contained"
              disableElevation
              disabled={isSubmitting || (currentPasswordValue ? !values.newPassword : !values.currentPassword)}
            >
              Next
            </Button>
          }
        >
          {!currentPasswordValue && (
            <>
              <Typography variant="subtitle1" sx={classes.verifyText(isMobile)}>
                To continue, please verify it's you:
              </Typography>

              <FormikTextField
                name="currentPassword"
                label="Current Password"
                type={showCurrentPassword ? 'text' : 'password'}
                endAdornment={endAdornment}
                variant="standard"
              />
            </>
          )}

          {currentPasswordValue && (
            <>
              <Typography variant="subtitle1" sx={classes.accountProtectText}>
                Protect your account by creating a password that meets the requirements.
              </Typography>

              <Typography variant="subtitle1" sx={classes.passwordMustTitle(isMobile)}>
                Password must:
              </Typography>

              <Box component="ul" sx={classes.requirementsList}>
                <Box component="li">
                  <Typography variant="caption" sx={classes.requirementItem}>
                    Be a minimum of 8 characters
                  </Typography>
                </Box>

                <Box component="li">
                  <Typography variant="caption" sx={classes.requirementItem}>
                    Include at least one number
                  </Typography>
                </Box>
              </Box>

              <FormikTextField
                name="newPassword"
                label="New Password"
                type={showNewPassword ? 'text' : 'password'}
                variant="standard"
                endAdornment={
                  <IconButton aria-label="toggle password visibility" onClick={() => setShowNewPassword((prev) => !prev)} edge="end">
                    {showNewPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                }
              />
            </>
          )}
        </FullScreenModal>
      )}
    </Formik>
  )
}

export default ChangePasswordModal
